import React, { useState } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TrendingUp, TrendingDown, ZoomIn, Download } from 'lucide-react';

interface DataPoint {
  name: string;
  value: number;
  [key: string]: any;
}

interface InteractiveChartProps {
  data: DataPoint[];
  title: string;
  type?: 'line' | 'bar' | 'area';
  onDrillDown?: (dataPoint: DataPoint) => void;
  drillDownEnabled?: boolean;
  exportEnabled?: boolean;
  isSpanish?: boolean;
}

export const InteractiveChart: React.FC<InteractiveChartProps> = ({
  data,
  title,
  type = 'line',
  onDrillDown,
  drillDownEnabled = false,
  exportEnabled = true,
  isSpanish = false,
}) => {
  const [chartType, setChartType] = useState(type);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [selectedPoint, setSelectedPoint] = useState<DataPoint | null>(null);

  const handlePointClick = (data: any) => {
    if (drillDownEnabled && onDrillDown && data && data.payload) {
      setSelectedPoint(data.payload);
      onDrillDown(data.payload);
    }
  };

  const handleExport = () => {
    const csvContent = [
      Object.keys(data[0]).join(','),
      ...data.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.toLowerCase().replace(/\s+/g, '-')}-data.csv`;
    a.click();
  };

  const calculateTrend = () => {
    if (data.length < 2) return 0;
    const first = data[0].value;
    const last = data[data.length - 1].value;
    return ((last - first) / first) * 100;
  };

  const trend = calculateTrend();
  const isPositiveTrend = trend > 0;

  const renderChart = () => {
    const commonProps = {
      data,
      margin: { top: 5, right: 30, left: 20, bottom: 5 },
    };

    const handleBarClick = (data: any) => {
      if (drillDownEnabled && data) {
        handlePointClick(data);
      }
    };

    switch (chartType) {
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: 'var(--radius)',
              }}
            />
            <Legend />
            <Bar 
              dataKey="value" 
              fill="hsl(var(--primary))"
              onClick={handleBarClick}
              onMouseEnter={(_: any, index: number) => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={hoveredIndex === index ? 'hsl(var(--primary))' : 'hsl(var(--primary) / 0.8)'}
                  cursor={drillDownEnabled ? 'pointer' : 'default'}
                />
              ))}
            </Bar>
          </BarChart>
        );
      
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.8} />
                <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: 'var(--radius)',
              }}
            />
            <Legend />
            <Area
              type="monotone"
              dataKey="value"
              stroke="hsl(var(--primary))"
              fillOpacity={1}
              fill="url(#colorValue)"
              onClick={handleBarClick}
            />
          </AreaChart>
        );
      
      default:
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: 'var(--radius)',
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="value"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              dot={{ r: 4, fill: 'hsl(var(--primary))' }}
              activeDot={{ 
                r: 6, 
                cursor: drillDownEnabled ? 'pointer' : 'default',
                onClick: handleBarClick
              }}
            />
          </LineChart>
        );
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="text-base">{title}</CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant={isPositiveTrend ? 'default' : 'destructive'}>
                {isPositiveTrend ? (
                  <TrendingUp className="w-3 h-3 mr-1" />
                ) : (
                  <TrendingDown className="w-3 h-3 mr-1" />
                )}
                {Math.abs(trend).toFixed(1)}%
              </Badge>
              {drillDownEnabled && (
                <Badge variant="outline">
                  <ZoomIn className="w-3 h-3 mr-1" />
                  {isSpanish ? 'Click para detalles' : 'Click for details'}
                </Badge>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Select value={chartType} onValueChange={(value: any) => setChartType(value)}>
              <SelectTrigger className="w-[120px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="line">{isSpanish ? 'Línea' : 'Line'}</SelectItem>
                <SelectItem value="bar">{isSpanish ? 'Barras' : 'Bar'}</SelectItem>
                <SelectItem value="area">{isSpanish ? 'Área' : 'Area'}</SelectItem>
              </SelectContent>
            </Select>
            
            {exportEnabled && (
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          {renderChart()}
        </ResponsiveContainer>
        
        {selectedPoint && (
          <div className="mt-4 p-3 bg-muted rounded-lg">
            <div className="text-sm font-medium mb-1">
              {isSpanish ? 'Punto seleccionado:' : 'Selected point:'}
            </div>
            <div className="text-sm text-muted-foreground">
              {selectedPoint.name}: <span className="font-medium">{selectedPoint.value}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
